"use client";

import { useEffect, useRef, useState } from "react";
import { X, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { Toast, FieldError } from "@/components/ui";
import { getAgeFromBirthdate } from "@/utils";
import { FORM_LIMITS } from "@/lib/formLimits";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: "signup" | "login";
}

export default function AuthModal({ isOpen, onClose, initialMode = "signup" }: AuthModalProps) {
  const { signIn, signUp } = useAuth();
  const { t } = useLanguage();
  const timeoutIdsRef = useRef<number[]>([]);
  // NavBar keeps <AuthModal> permanently mounted and only toggles `isOpen`,
  // so this component's state survives a close/reopen. Every fresh open bumps
  // this counter; handleSubmit snapshots it and bails if it changed while an
  // in-flight request was resolving (see the guard after `await signIn`).
  const openGenerationRef = useRef(0);
  const [mode, setMode] = useState<"signup" | "login">(initialMode);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [location, setLocation] = useState("");
  const [ageGroup, setAgeGroup] = useState<"age_18_plus" | "under_18" | null>(null);
  const [birthdate, setBirthdate] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [emailAlreadyRegistered, setEmailAlreadyRegistered] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // A fresh open is a fresh session: bump the generation and clear any
      // transient state left over from the previous open (a stale toast or a
      // stuck `loading` would otherwise bleed through since we never unmount).
      openGenerationRef.current += 1;
      setMode(initialMode);
      setToast(null);
      setLoading(false);
      setSubmitAttempted(false);
      setEmailAlreadyRegistered(false);
    }
  }, [isOpen, initialMode]);

  // Validation states
  const [touched, setTouched] = useState({
    fullName: false,
    email: false,
    password: false,
  });

  const isValidEmail = (emailStr: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailStr);
  const passwordChecks = {
    length: password.length >= FORM_LIMITS.password.min,
    number: /\d/.test(password),
    special: /[^A-Za-z0-9]/.test(password),
    uppercase: /[A-Z]/.test(password),
  };
  const isPasswordTooLong = password.length > FORM_LIMITS.password.max;
  const isValidPassword =
    passwordChecks.length &&
    passwordChecks.number &&
    passwordChecks.special &&
    passwordChecks.uppercase &&
    !isPasswordTooLong;
  const isValidName = fullName.trim().length > 0;
  const getBirthdateError = (): string | null => {
    if (ageGroup !== "under_18") return null;
    if (!birthdate) return t.errors.requiredField;

    const age = getAgeFromBirthdate(birthdate);
    if (age === null) return t.auth.birthdateFutureError;
    if (age < 13 || age > 17) return t.auth.birthdateAgeRangeError;

    return null;
  };
  const birthdateError = getBirthdateError();
  const isAgeGroupSelected = ageGroup !== null;
  const isValidBirthdate = ageGroup === "under_18" ? birthdateError === null : true;

  // Fields gating the submit button itself — only typed/selected fields that
  // the user clearly knows are wrong. The age-group radio and consent checkbox
  // are validated inside handleSubmit so the existing missing-fields banner is
  // actually reachable when the user clicks submit.
  const isSignupFormValid = isValidName && isValidEmail(email) && isValidPassword && location.length > 0 && isValidBirthdate;
  const canSubmitSignup = isSignupFormValid && isAgeGroupSelected && consentAccepted;
  const isLoginFormValid = isValidEmail(email) && password.length > 0;

  const clearPendingTimeouts = () => {
    timeoutIdsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
    timeoutIdsRef.current = [];
  };

  const scheduleTimeout = (callback: () => void, delay: number) => {
    const timeoutId = window.setTimeout(() => {
      timeoutIdsRef.current = timeoutIdsRef.current.filter((id) => id !== timeoutId);
      callback();
    }, delay);

    timeoutIdsRef.current.push(timeoutId);
  };

  const handleClose = () => {
    clearPendingTimeouts();
    onClose();
  };

  useEffect(() => clearPendingTimeouts, []);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    // Double-click guard. `disabled={loading}` only renders after the React
    // commit; a fast second click can fire before that, queuing a duplicate
    // signUp/signIn request.
    if (loading) return;

    if (mode === "signup" && !canSubmitSignup) {
      setSubmitAttempted(true);
      return;
    }
    if (mode === "login" && !isLoginFormValid) return;

    // Which open-session this submit belongs to. If the modal is closed and
    // reopened while signIn/signUp is still in flight, the resolved handler
    // must not write its result into that newer session.
    const submitGeneration = openGenerationRef.current;

    clearPendingTimeouts();
    setLoading(true);
    setToast(null);

    if (mode === "signup") {
      if (!ageGroup) {
        setLoading(false);
        return;
      }

      const result = await signUp({
        email,
        password,
        displayName: fullName,
        location,
        ageGroup,
        birthdate: birthdate || undefined,
      });

      if (result.success) {
        setToast({ message: t.auth.accountCreatedSuccess || 'Account created successfully!', type: 'success' });
        scheduleTimeout(() => {
          if (result.requiresVerification) {
            // Redirect to verification page
            window.location.href = `/verify-email?email=${encodeURIComponent(result.email || email)}`;
          } else {
            // Old flow (shouldn't happen with new setup)
            setMode("login");
            setPassword("");
            setToast(null);
          }
        }, 1500);
      } else if (result.error === 'EMAIL_ALREADY_REGISTERED') {
        // Render an inline banner with a Sign In CTA rather than a toast, so
        // the user has a clear path forward (MWBI-729).
        setEmailAlreadyRegistered(true);
      } else {
        setToast({ message: result.error || t.auth.failedToCreateAccount, type: 'error' });
        scheduleTimeout(() => setToast(null), 4000);
      }
    } else {
      const result = await signIn({ email, password });

      // On a successful login the NavBar effect closes this modal as soon as
      // `currentUser` is set — which can happen before this await resolves —
      // and the modal may already have been reopened for a fresh attempt.
      // Since AuthModal is never unmounted (NavBar only toggles `isOpen`), a
      // toast set or handleClose scheduled here would bleed into that newer
      // session: a stale "Login successful!" that auto-closes the new modal.
      // If this submit belongs to a previous open-session, stop here.
      if (openGenerationRef.current !== submitGeneration) return;

      if (result.success) {
        setToast({ message: t.auth.loginSuccess || 'Login successful!', type: 'success' });
        scheduleTimeout(handleClose, 1000);
      } else {
        setToast({ message: result.error || t.auth.invalidCredentials, type: 'error' });
        scheduleTimeout(() => setToast(null), 4000);
      }
    }

    setLoading(false);
  };

  const resetForm = () => {
    setFullName("");
    setEmail("");
    setPassword("");
    setLocation("");
    setAgeGroup(null);
    setBirthdate("");
    setShowPassword(false);
    setSubmitAttempted(false);
    setConsentAccepted(false);
    setTouched({ fullName: false, email: false, password: false });
  };

  const switchMode = (newMode: "signup" | "login") => {
    clearPendingTimeouts();
    resetForm();
    setToast(null);
    setEmailAlreadyRegistered(false);
    setMode(newMode);
  };

  const inputClass = "w-full px-3 py-2.5 border border-border rounded-md text-sm text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent";
  const labelClass = "block text-sm font-medium text-foreground mb-1";
  const helperClass = "text-xs text-caption mt-1";
  const errorClass = "text-xs text-red-500 mt-1";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="auth-modal-root">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
      <div className="relative bg-background rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-1 hover:bg-sidebar-hover rounded transition z-10"
          aria-label="Close"
          data-testid="auth-modal-close-btn"
        >
          <X size={20} className="text-foreground-muted" />
        </button>

        {/* Content */}
        <div className="p-6 pt-8">
          {mode === "signup" ? (
            <SignupForm
              fullName={fullName}
              setFullName={setFullName}
              email={email}
              setEmail={setEmail}
              password={password}
              setPassword={setPassword}
              location={location}
              setLocation={setLocation}
              ageGroup={ageGroup}
              setAgeGroup={setAgeGroup}
              birthdate={birthdate}
              setBirthdate={setBirthdate}
              showPassword={showPassword}
              setShowPassword={setShowPassword}
              touched={touched}
              setTouched={setTouched}
              isValidEmail={isValidEmail}
              isValidPassword={isValidPassword}
              passwordChecks={passwordChecks}
              isValidName={isValidName}
              isFormValid={isSignupFormValid}
              loading={loading}
              handleSubmit={handleSubmit}
              switchMode={() => switchMode("login")}
              labelClass={labelClass}
              helperClass={helperClass}
              errorClass={errorClass}
              birthdateError={birthdateError}
              submitAttempted={submitAttempted}
              consentAccepted={consentAccepted}
              setConsentAccepted={setConsentAccepted}
              emailAlreadyRegistered={emailAlreadyRegistered}
              onSignInInstead={() => switchMode("login")}
              t={t}
            />
          ) : (
            <LoginForm
              email={email}
              setEmail={setEmail}
              password={password}
              setPassword={setPassword}
              showPassword={showPassword}
              setShowPassword={setShowPassword}
              touched={touched}
              setTouched={setTouched}
              isValidEmail={isValidEmail}
              isFormValid={isLoginFormValid}
              loading={loading}
              handleSubmit={handleSubmit}
              switchMode={() => switchMode("signup")}
              inputClass={inputClass}
              labelClass={labelClass}
              helperClass={helperClass}
              errorClass={errorClass}
              t={t}
            />
          )}
        </div>
      </div>

      {/* Toast Notification */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

interface SignupFormProps {
  fullName: string;
  setFullName: (v: string) => void;
  email: string;
  setEmail: (v: string) => void;
  password: string;
  setPassword: (v: string) => void;
  location: string;
  setLocation: (v: string) => void;
  ageGroup: "age_18_plus" | "under_18" | null;
  setAgeGroup: (v: "age_18_plus" | "under_18" | null) => void;
  birthdate: string;
  setBirthdate: (v: string) => void;
  showPassword: boolean;
  setShowPassword: (v: boolean) => void;
  touched: { fullName: boolean; email: boolean; password: boolean };
  setTouched: (v: { fullName: boolean; email: boolean; password: boolean }) => void;
  isValidEmail: (email: string) => boolean;
  isValidPassword: boolean;
  passwordChecks: { length: boolean; number: boolean; special: boolean; uppercase: boolean };
  isValidName: boolean;
  isFormValid: boolean;
  loading: boolean;
  handleSubmit: () => void;
  switchMode: () => void;
  labelClass: string;
  helperClass: string;
  errorClass: string;
  birthdateError: string | null;
  submitAttempted: boolean;
  consentAccepted: boolean;
  setConsentAccepted: (v: boolean) => void;
  emailAlreadyRegistered: boolean;
  onSignInInstead: () => void;
  t: ReturnType<typeof useLanguage>['t'];
}

function SignupForm({
  fullName, setFullName, email, setEmail, password, setPassword,
  location, setLocation, ageGroup, setAgeGroup, birthdate, setBirthdate,
  showPassword, setShowPassword,
  touched, setTouched, isValidEmail, isValidPassword, passwordChecks, isValidName,
  isFormValid, loading, handleSubmit, switchMode,
  labelClass, helperClass, errorClass, birthdateError, submitAttempted,
  consentAccepted, setConsentAccepted, emailAlreadyRegistered, onSignInInstead, t
}: SignupFormProps) {
  const isAgeGroupSelected = ageGroup !== null;

  const getFieldClass = (isValid: boolean) => {
    const base = "w-full px-3 py-2.5 border rounded-md text-sm text-foreground bg-background focus:outline-none focus:ring-2 focus:border-transparent";
    if (submitAttempted && !isValid) {
      return `${base} border-red-500 focus:ring-red-500`;
    }
    return `${base} border-border focus:ring-primary`;
  };

  return (
    <>
      {/* Header */}
      <div className="text-center mb-2">
        <h2 className="text-2xl font-bold text-foreground">{t.auth.createAccount}</h2>
      </div>
      {/* Switch to Login */}
      <p className="text-center text-sm text-foreground mt-1 mb-4">
        {t.auth.alreadyHaveAccount}{" "}
        <button onClick={switchMode} className="text-lg text-link-blue font-semibold hover:underline" data-testid="auth-modal-switch-mode-link">
          {t.auth.signIn}
        </button>
      </p>

      {/* Social Auth Buttons */}
      <div className="space-y-3 mb-4">
        <button
          type="button"
          onClick={() => { window.location.href = '/api/auth/google'; }}
          className="w-full flex items-center justify-center gap-3 px-4 py-2.5 border border-border rounded-md text-sm font-medium text-foreground bg-background hover:bg-sidebar-hover transition"
          data-testid="auth-modal-google-btn"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {t.auth.continueWithGoogle}
        </button>
      </div>

      {/* Divider */}
      <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-caption uppercase tracking-wide">{t.auth.orContinueWith}</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      {/* Duplicate-email banner with a Sign In CTA (MWBI-729). */}
      {emailAlreadyRegistered && (
        <div
          role="alert"
          data-testid="auth-modal-email-already-registered"
          className="flex items-center justify-between gap-3 p-3 mb-4 border border-red-300 bg-red-50 rounded-md"
        >
          <p className="text-sm text-red-700 font-medium">{t.auth.emailAlreadyRegistered}</p>
          <button
            type="button"
            onClick={onSignInInstead}
            data-testid="auth-modal-sign-in-instead-btn"
            className="text-sm font-medium text-primary hover:underline whitespace-nowrap"
          >
            {t.auth.signInInstead}
          </button>
        </div>
      )}

      {/* Missing fields error */}
      {submitAttempted && !isFormValid && (
        <p className="text-sm text-red-500 mb-4 font-medium" data-testid="auth-modal-missing-fields-error">{t.auth.missingFieldsError}</p>
      )}

      {/* Form Fields */}
      <div className="space-y-4">
        {/* Full Name */}
        <div>
          <label className={labelClass}>
            {t.auth.yourName} <span className="text-red-500">*</span>
          </label>
          <input
            id="auth-modal-fullname-input"
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            onBlur={() => setTouched({ ...touched, fullName: true })}
            className={getFieldClass(isValidName)}
            maxLength={FORM_LIMITS.signupDisplayName.max}
            aria-invalid={(touched.fullName || submitAttempted) && !isValidName}
            aria-describedby={
              (touched.fullName || submitAttempted) && !isValidName
                ? "auth-modal-fullname-error"
                : undefined
            }
            data-testid="auth-modal-fullname"
          />
          {(touched.fullName || submitAttempted) && !isValidName && (
            <FieldError
              id="auth-modal-fullname-error"
              data-testid="auth-modal-fullname-error"
            >
              {t.errors.nameRequired}
            </FieldError>
          )}
        </div>

        {/* Email */}
        <div>
          <label className={labelClass}>
            {t.auth.email} <span className="text-red-500">*</span>
          </label>
          <input
            id="auth-modal-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setTouched({ ...touched, email: true })}
            placeholder="example@email.com"
            className={getFieldClass(isValidEmail(email))}
            aria-invalid={(touched.email || submitAttempted) && !isValidEmail(email)}
            aria-describedby={
              (touched.email || submitAttempted) && !isValidEmail(email)
                ? "auth-modal-email-error"
                : undefined
            }
            data-testid="auth-modal-email"
          />
          {(touched.email || submitAttempted) && !isValidEmail(email) && (
            <FieldError
              id="auth-modal-email-error"
              data-testid="auth-modal-email-error"
            >
              {email.length === 0 ? t.errors.emailRequired : t.errors.emailInvalid}
            </FieldError>
          )}
        </div>

        {/* Password */}
        <div>
          <label className={labelClass}>
            {t.auth.password} <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              id="auth-modal-password-input"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={() => setTouched({ ...touched, password: true })}
              className={getFieldClass(isValidPassword)}
              maxLength={FORM_LIMITS.password.max}
              aria-invalid={(touched.password || submitAttempted) && !isValidPassword}
              aria-describedby={
                (touched.password || submitAttempted) && !isValidPassword
                  ? "auth-modal-password-error"
                  : undefined
              }
              data-testid="auth-modal-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-caption hover:text-foreground"
              data-testid="auth-modal-password-toggle"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {(touched.password || submitAttempted) && password.length === 0 && (
            <FieldError
              id="auth-modal-password-error"
              data-testid="auth-modal-password-error"
            >
              {t.errors.passwordRequired}
            </FieldError>
          )}
          {password.length > FORM_LIMITS.password.max && (
            <FieldError data-testid="auth-modal-password-too-long-error">
              {t.errors.passwordTooLong}
            </FieldError>
          )}
          <ul className="mt-1 space-y-0.5">
            {([
              ['length', t.auth.passwordMinLength],
              ['uppercase', t.auth.passwordReqUppercase],
              ['number', t.auth.passwordReqNumber],
              ['special', t.auth.passwordReqSpecial],
            ] as const).map(([key, label]) => {
              const met = passwordChecks[key];
              const showError = (touched.password || submitAttempted) && !met;
              return (
                <li key={key} className={`text-xs ${showError ? 'text-red-500' : met ? 'text-green-600' : 'text-caption'}`}>
                  {met ? '\u2713' : '\u2022'} {label}
                </li>
              );
            })}
          </ul>
        </div>

        {/* Location Dropdown */}
        <div>
          <label className={labelClass}>
            {t.auth.location} <span className="text-red-500">*</span>
          </label>
          <select
            id="auth-modal-location-input"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            className={getFieldClass(location.length > 0)}
            aria-invalid={submitAttempted && location.length === 0}
            aria-describedby={
              submitAttempted && location.length === 0
                ? "auth-modal-location-error"
                : undefined
            }
            data-testid="auth-modal-location"
          >
            <option value="" disabled>Select location</option>
            <option value="Hailey">Hailey</option>
            <option value="Ketchum">Ketchum</option>
            <option value="Bellevue">Bellevue</option>
            <option value="Carey">Carey</option>
            <option value="Sun Valley">Sun Valley</option>
            <option value="Custer County">Custer County</option>
            <option value="Lincoln County">Lincoln County</option>
            <option value="Camas County">Camas County</option>
            <option value="Butte County">Butte County</option>
            <option value="Picabo">Picabo</option>
            <option value="Blaine County">Blaine County</option>
            <option value="Other">Other</option>
          </select>
          {submitAttempted && location.length === 0 && (
            <FieldError
              id="auth-modal-location-error"
              data-testid="auth-modal-location-error"
            >
              {t.errors.locationRequired}
            </FieldError>
          )}
        </div>

        {/* Age Verification - Radio Buttons */}
        <div>
          <label className={labelClass}>
            {t.auth.age} <span className="text-red-500">*</span>
          </label>
          <div
            role="radiogroup"
            aria-invalid={submitAttempted && !isAgeGroupSelected}
            aria-describedby={
              submitAttempted && !isAgeGroupSelected
                ? "auth-modal-age-group-error"
                : undefined
            }
            className={`space-y-2 ${submitAttempted && !isAgeGroupSelected ? 'p-2 border border-red-500 rounded-md' : ''}`}
          >
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="ageGroup"
                checked={ageGroup === "under_18"}
                onChange={() => setAgeGroup("under_18")}
                className="w-4 h-4 border-border text-primary focus:ring-primary"
                data-testid="auth-modal-age-under-18"
              />
              <span className="text-sm text-foreground">{t.auth.ageConfirmation13to15}</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="ageGroup"
                checked={ageGroup === "age_18_plus"}
                onChange={() => { setAgeGroup("age_18_plus"); setBirthdate(""); }}
                className="w-4 h-4 border-border text-primary focus:ring-primary"
                data-testid="auth-modal-age-18-plus"
              />
              <span className="text-sm text-foreground">{t.auth.ageConfirmation16Plus}</span>
            </label>
          </div>
          {submitAttempted && !isAgeGroupSelected && (
            <FieldError
              id="auth-modal-age-group-error"
              data-testid="auth-modal-age-group-error"
            >
              {t.errors.ageGroupRequired}
            </FieldError>
          )}
        </div>

        {/* Birthdate - Only shown for users under 18 */}
        {ageGroup === "under_18" && (
          <div>
            <label className={labelClass}>{t.auth.birthdate}</label>
            <input
              type="date"
              value={birthdate}
              onChange={(e) => setBirthdate(e.target.value)}
              className={getFieldClass(birthdateError === null)}
              data-testid="auth-modal-birthdate"
            />
            {birthdateError ? (
              <p className={errorClass}>{birthdateError}</p>
            ) : (
              <p className={helperClass}>{t.auth.birthdateHelper}</p>
            )}
          </div>
        )}

        {/* Terms / Privacy / Community Guidelines consent */}
        <div>
          <div className={`flex items-start gap-2 text-sm ${submitAttempted && !consentAccepted ? 'p-2 border border-red-500 rounded-md' : ''}`}>
            <input
              id="auth-modal-consent"
              type="checkbox"
              checked={consentAccepted}
              onChange={(e) => setConsentAccepted(e.target.checked)}
              className="mt-1 w-4 h-4 border-border text-primary focus:ring-primary cursor-pointer"
              data-testid="auth-modal-consent-checkbox"
              aria-required="true"
              aria-describedby={submitAttempted && !consentAccepted ? 'auth-modal-consent-error' : undefined}
            />
            <span className="text-foreground">
              <span className="text-red-500 mr-1" aria-hidden="true">*</span>
              {/* Anchors are siblings of <label>, not descendants — clicking a link must not toggle the checkbox. */}
              <label htmlFor="auth-modal-consent" className="cursor-pointer">{t.auth.consentLabel}</label>{" "}
              <a href="/guidelines-legal?tab=terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{t.auth.termsOfService}</a>
              {", "}
              <a href="/guidelines-legal?tab=privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{t.auth.privacyPolicy}</a>
              {" "}{t.auth.and}{" "}
              <a href="/guidelines-legal?tab=community-guidelines" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{t.auth.communityGuidelines}</a>
              .
            </span>
          </div>
          {submitAttempted && !consentAccepted && (
            <p id="auth-modal-consent-error" className={errorClass} data-testid="auth-modal-consent-error">{t.auth.consentRequired}</p>
          )}
        </div>
      </div>

      {/* Submit Button */}
      <button
        onClick={handleSubmit}
        disabled={loading}
        className="w-full mt-6 px-4 py-3 bg-primary text-white rounded-md text-sm font-medium hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="auth-modal-submit-btn"
      >
        {loading ? t.auth.creatingAccount : t.auth.signUp}
      </button>
    </>
  );
}

interface LoginFormProps {
  email: string;
  setEmail: (v: string) => void;
  password: string;
  setPassword: (v: string) => void;
  showPassword: boolean;
  setShowPassword: (v: boolean) => void;
  touched: { fullName: boolean; email: boolean; password: boolean };
  setTouched: (v: { fullName: boolean; email: boolean; password: boolean }) => void;
  isValidEmail: (email: string) => boolean;
  isFormValid: boolean;
  loading: boolean;
  handleSubmit: () => void;
  switchMode: () => void;
  inputClass: string;
  labelClass: string;
  helperClass: string;
  errorClass: string;
  t: ReturnType<typeof useLanguage>['t'];
}

function LoginForm({
  email, setEmail, password, setPassword,
  showPassword, setShowPassword, touched, setTouched,
  isValidEmail, isFormValid, loading, handleSubmit, switchMode,
  inputClass, labelClass, errorClass, t
}: LoginFormProps) {
  return (
    <>
      {/* Header */}
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-foreground">{t.auth.welcomeBack}</h2>
        <p className="text-sm text-primary mt-1">{t.auth.signInToContinue}</p>
      </div>

      {/* Social Auth Buttons */}
      <div className="space-y-3 mb-4">
        <button
          type="button"
          onClick={() => { window.location.href = '/api/auth/google'; }}
          className="w-full flex items-center justify-center gap-3 px-4 py-2.5 border border-border rounded-md text-sm font-medium text-foreground bg-background hover:bg-sidebar-hover transition"
          data-testid="auth-modal-google-btn"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {t.auth.continueWithGoogle}
        </button>
      </div>

      {/* Divider */}
      <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-caption uppercase tracking-wide">{t.auth.orContinueWith}</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      {/* Form Fields */}
      <div className="space-y-4">
        {/* Email */}
        <div>
          <label className={labelClass}>{t.auth.email}</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setTouched({ ...touched, email: true })}
            placeholder="example@email.com"
            className={inputClass}
            data-testid="auth-modal-email"
          />
          {touched.email && !isValidEmail(email) && (
            <p className={errorClass}>{t.auth.invalidEmail}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <label className={labelClass}>{t.auth.password}</label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={() => setTouched({ ...touched, password: true })}
              className={inputClass}
              data-testid="auth-modal-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-caption hover:text-foreground"
              data-testid="auth-modal-password-toggle"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>
      </div>

      {/* Forgot Password Link */}
      <div className="text-right -mt-2">
        <a
          href="/forgot-password"
          className="text-xs text-caption hover:text-primary transition"
          data-testid="auth-modal-forgot-pw-link"
        >
          {t.auth.forgotPassword}
        </a>
      </div>

      {/* Submit Button */}
      <button
        onClick={handleSubmit}
        disabled={!isFormValid || loading}
        className="w-full mt-6 px-4 py-3 bg-primary text-white rounded-md text-sm font-medium hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="auth-modal-submit-btn"
      >
        {loading ? t.auth.loggingIn : t.auth.logIn}
      </button>

      {/* Switch to Signup */}
      <p className="text-center text-sm text-foreground mt-6">
        {t.auth.dontHaveAccount}{" "}
        <button onClick={switchMode} className="text-primary font-medium hover:underline" data-testid="auth-modal-switch-mode-link">
          {t.auth.signUp}
        </button>
      </p>
    </>
  );
}


